import {useEffect, useMemo, useRef, useState} from "react";
import {useDebounce} from "use-debounce";
import Icon from "../selfcare/Icon";
import {
    fetchSearchResults,
    fetchSummary,
    fetchTopQuestions,
    SearchResultsResponse,
    SummaryResponse,
    TopQuestionsResponse
} from "../../util/raffleRequests.ts";
import {v4 as uuidv4} from 'uuid';
import {useClickOutside} from "../useClickOutside.tsx";
import {uid} from "./uid.ts";
import {Link} from "react-router-dom";
import {OpeningHours, PhoneNumbers} from "../../util/contact.ts";
import IconArrow from "../icons/IconArrow.tsx";

// for now only /hjaelp is relative
const regex = /https:\/\/www\.hiper\.dk(\/hjaelp.*)/;

const makeUrlRelative = (link: string): string => {
    let m;

    if ((m = regex.exec(link)) !== null) {
        if (m.length === 2) {
            return m[1]
        }
    }

    return link
}

export default function RaffleSearchComponent(props: { uid: string }) {
    const [query, setQuery] = useState("");
    const [queryBounced] = useDebounce(query, 500);
    const [topQuestions, setTopQuestions] = useState<undefined | TopQuestionsResponse>();
    const [searchResults, setSearchResults] = useState<SearchResultsResponse | undefined>(undefined);
    const [loadingSearchResults, setLoadingSearchResults] = useState<boolean>(false);
    const [showContactOptions, setShowContactOptions] = useState<boolean>(false);
    const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
    const [summary, setSummary] = useState<SummaryResponse | undefined>(undefined);
    const [showSearchResults, setShowSearchResults] = useState<boolean>(true);
    const [focus, setFocus] = useState<boolean>(false);
    const [showPhoneNumberDetails, setShowPhoneNumberDetails] = useState<boolean>(false);
    const raffleSessionIdentifier = useMemo(() => uuidv4(), [])
    const ref = useRef<HTMLDivElement>(null)

    useClickOutside(ref, () => {
        setShowSearchResults(false)
    })

    const summaryWithReplacedReferences = useMemo(() => {
        if (summary === undefined) {
            return undefined
        }

        let s = summary.summary

        summary.references.forEach((reference) => {
            s = s.replaceAll(`[${reference.index}]`, `<a class="reference" href="${reference.url}">${reference.index}</a>`)
        })

        return s
    }, [summary])

    useEffect(() => {
        setFocus(false)
    }, [query])

    useEffect(() => {
        setSearchResults(undefined)
        setSummary(undefined)

        if (queryBounced.trim()) {
            setLoadingSearchResults(true)
            setLoadingSummary(true)

            fetchSearchResults(props.uid, queryBounced, "unknown", raffleSessionIdentifier).then((results) => {
                setSearchResults(results)
                setLoadingSearchResults(false)
            })

            fetchSummary(props.uid, queryBounced).then((results) => {
                setSummary(results)
                setLoadingSummary(false)
            })
        }
    }, [props.uid, queryBounced, raffleSessionIdentifier])

    useEffect(() => {
        fetchTopQuestions(props.uid).then((results) => {
            setTopQuestions(results)
        })
    }, [props.uid])

    return (
        <div ref={ref} className={'search-input-container'}>
            <input
                className={'search-input'}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                    setFocus(true)
                    setShowSearchResults(true)
                }}
                onBlur={() => setFocus(false)}
                placeholder="Skriv dit spørgsmål her"
            />

            <div className={'post-input-container'}>
                {query.length === 0 && focus && (
                    <div className={'top-questions'}>
                        {topQuestions === undefined ? (
                            <p>Indlæser spørgsmål<span className={'form__loader'}/></p>
                        ) : (
                            <ul>
                                {topQuestions.questions.map(({question}) => (
                                    <li
                                        key={question} onMouseDown={() => {
                                        setQuery(question)
                                    }}
                                    >
                                        <span className={'text'}>{question}</span>
                                        <Icon component={IconArrow}/>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {queryBounced.length > 0 && showSearchResults && !showContactOptions && (
                    <div className={'search-summary'}>
                        <h3>HiBot opsummering</h3>
                        {loadingSummary && (
                            <div className={'summary'}>
                                <p>Genererer svar<span className={'form__loader'}/></p>
                            </div>
                        )}
                        {summary !== undefined && summary.status === "success" && summaryWithReplacedReferences && (
                            <div className={'summary'} dangerouslySetInnerHTML={{__html: summaryWithReplacedReferences}}/>
                        )}
                        {summary !== undefined && summary.status === "no-relevant-results" && (
                            <div className={'summary'}>
                                <p>HiBot fandt ingen relevante svar.</p>
                            </div>
                        )}
                    </div>
                )}

                {queryBounced.length > 0 && showSearchResults && !showContactOptions && (
                    <div className={'relevant-articles'}>
                        <h3>HiBot foreslår disse artikler</h3>
                        {loadingSearchResults && (
                            <p>Finder artikler<span className={'form__loader'}/></p>
                        )}
                        {searchResults !== undefined && searchResults.results.length > 0 && (
                            <ul>
                                {searchResults.results.filter((result) => result.type === "paragraph").map((result, index) => (
                                        <li key={index}>
                                            <Link to={makeUrlRelative(result.url)}>
                                                <span className={'text'}>{result.title}</span>
                                                <Icon component={IconArrow}/>
                                            </Link>
                                        </li>
                                    )
                                )}
                            </ul>
                        )}
                        {searchResults !== undefined && searchResults.results.length === 0 && (
                            <p>Kunne ikke finde nogen relevante artikler.</p>
                        )}
                    </div>
                )}

                {queryBounced.length > 0 && showSearchResults && (
                    <div className={'contact-options'}>
                        {showContactOptions && (
                            <>
                                <h3>Kontakt os</h3>
                                <p>Kontakt vores kundeservice via en af mulighederne nedenfor.</p>
                                <button
                                    className={'contact-button'}
                                    onClick={() => {
                                        window.raffleApi.hide(uid.chat)
                                        window.zE.activate()
                                    }}
                                >
                                    <strong>Chat med os</strong><br/>
                                    <small>Hverdage kl. {OpeningHours.Chat.MonFri} & weekend&nbsp;kl.&nbsp;{OpeningHours.Chat.SunSat}</small>
                                </button>
                                <button className={'contact-button'} onClick={() => setShowPhoneNumberDetails((p) => !p)}>
                                    <strong>Ring til os</strong><br/>
                                    <small>Man-fre kl. {OpeningHours.Phone}</small>
                                </button>
                                {showPhoneNumberDetails && (
                                    <a className={'phone-number'} href={`tel:+45${PhoneNumbers.Raffle}`}>
                                        {PhoneNumbers.Raffle}
                                    </a>
                                )}
                            </>
                        )}
                        <button
                            className={`toggle-contact-button ${showContactOptions ? 'back' : ''}`}
                            onClick={() => setShowContactOptions((p) => !p)}
                        >
                            <span className={'text'}>
                                {showContactOptions ? 'Tilbage' : 'Kontakt os'}
                            </span>
                            <Icon component={IconArrow}/>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
